import { observer } from 'mobx-react-lite'
import { MdOutlinePriorityHigh } from 'react-icons/md'

import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { cx } from '@/Ship/Helpers/cssClass'

import { REQUEST_STATUS_MAP } from '../Contracts/RequestStatus'
import { formatDate } from '../Helpers/formatDate'
import { statusOption } from '../Helpers/statusOption'
import { formatVolume, summarizeVolumes } from '../Helpers/volumeTotals'
import type { VehicleNeedsEditStore } from '../Stores/VehicleNeedsEditStore'

import styles from './RequestFormTemplate.module.css'

interface RequestSummaryProps {
    formContext: any
    /** У редагуванні — незбережені зміни завдань впливають на «розподілено» */
    editStore?: VehicleNeedsEditStore | undefined
}

const personName = (person: any): string | null => person?.shortName || person?.fullName || null

/**
 * Зведення заявки: номер, статус, «Терміново», ключові факти і по одній плашці
 * «Розподілено X з Y» на одиницю виміру потреб.
 */
export const RequestSummary = observer(({ formContext, editStore }: RequestSummaryProps) => {
    const v: any = formContext?.values ?? {}
    const number = v.erpNumber || v.externalCode
    const title = v.id ? `Заявка #${v.id}${number ? ` · ${number}` : ''}` : 'Заявка'
    const erpDate = formatDate(v.erpCreatedAt)
    const meta = [
        v.constructionObject?.title,
        personName(v.responsibleCustomer),
        erpDate ? `в ERP з ${erpDate}` : null,
    ].filter(Boolean) as string[]

    const totals = summarizeVolumes(
        v.vehicleNeeds,
        editStore
            ? { pendingChanges: editStore.pendingChanges, assignmentsByExecutor: editStore.localAssignmentsByExecutor }
            : {}
    )

    return (
        <div className={cx(styles, 'summary')}>
            <div className={cx(styles, 'summaryMain')}>
                <div className={cx(styles, 'summaryTitle')}>
                    <span className={cx(styles, 'summaryId')}>{title}</span>
                    {v.status && (
                        <StatusBadge option={statusOption(REQUEST_STATUS_MAP, v.status)} fallbackLabel={v.status} />
                    )}
                    {v.isUrgent && (
                        <span className={cx(styles, 'urgent')}>
                            <MdOutlinePriorityHigh aria-hidden="true" />
                            Терміново
                        </span>
                    )}
                </div>
                {meta.length > 0 && (
                    <div className={cx(styles, 'summaryMeta')}>
                        {meta.map((item) => (
                            <span key={item}>{item}</span>
                        ))}
                    </div>
                )}
            </div>
            {totals.length > 0 && (
                <div className={cx(styles, 'volumes')} role="group" aria-label="Розподіл обсягу">
                    {totals.map(({ unit, ordered, distributed }) => {
                        // Порівняння в тисячних: сума float-мас (0.1 + 0.2) не має давати «недорозподілено»
                        const orderedMilli = Math.round(ordered * 1000)
                        const distributedMilli = Math.round(distributed * 1000)
                        const percent = orderedMilli > 0 ? Math.min(100, (distributedMilli / orderedMilli) * 100) : 0
                        const done = orderedMilli > 0 && distributedMilli >= orderedMilli
                        return (
                            <div key={unit} className={cx(styles, 'volume')}>
                                <span className={cx(styles, 'volumeText')}>
                                    Розподілено <strong>{formatVolume(distributed)}</strong> з {formatVolume(ordered)}{' '}
                                    {unit}
                                </span>
                                <div
                                    className={cx(styles, 'volumeBar')}
                                    role="progressbar"
                                    aria-label={`Розподілено, ${unit}`}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-valuenow={Math.round(percent)}
                                >
                                    <div
                                        className={`${cx(styles, 'volumeFill')} ${done ? cx(styles, 'volumeFillDone') : ''}`}
                                        style={{ width: `${percent}%` }}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
})
